import supabase from "@/services/supabaseClient";

export const APPOINTMENT_CHANGE_EVENT_TYPES = {
  RESCHEDULE_REQUESTED: "reschedule_requested",
  RESCHEDULE_APPROVED: "reschedule_approved",
  RESCHEDULE_APPLIED: "reschedule_applied",
};

const RESCHEDULE_PAYLOAD_FIELDS = [
  "appointment_date",
  "consultation_type",
  "practice_id",
  "duration_minutes",
  "booked_service_id",
  "booked_service_name",
  "booked_service_price",
  "booked_service_currency",
  "status",
];

function safeIso(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

export function getAppointmentDateIso(value) {
  return safeIso(value);
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function sanitizeReschedulePayload(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;

  const clean = {};

  for (const key of RESCHEDULE_PAYLOAD_FIELDS) {
    if (!hasOwn(payload, key)) continue;
    const value = payload[key];

    if (key === "appointment_date") {
      const iso = safeIso(value);
      if (iso) clean[key] = iso;
      continue;
    }

    if (key === "duration_minutes") {
      const parsed = Number(value);
      if (Number.isFinite(parsed) && parsed > 0) clean[key] = parsed;
      continue;
    }

    if (key === "booked_service_price") {
      if (value == null || value === "") {
        clean[key] = null;
        continue;
      }
      const parsed = Number(value);
      if (Number.isFinite(parsed)) clean[key] = parsed;
      continue;
    }

    if (key === "practice_id" || key === "booked_service_id") {
      clean[key] = value ? String(value).trim() || null : null;
      continue;
    }

    if (
      key === "consultation_type" ||
      key === "booked_service_name" ||
      key === "booked_service_currency" ||
      key === "status"
    ) {
      clean[key] = value == null ? null : String(value).trim() || null;
      continue;
    }
  }

  return Object.keys(clean).length ? clean : null;
}

export async function createAppointmentChangeEvent({
  appointmentId,
  eventType,
  oldAppointmentDate = null,
  newAppointmentDate = null,
  actorUserId,
  payload = null,
}) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  const cleanEventType = String(eventType || "").trim().toLowerCase();
  const cleanActorUserId = String(actorUserId || "").trim();
  if (!cleanAppointmentId || !cleanEventType || !cleanActorUserId) {
    throw new Error("Missing appointment change event context.");
  }

  const eventPayload = {
    appointment_id: cleanAppointmentId,
    event_type: cleanEventType,
    old_appointment_date: safeIso(oldAppointmentDate) || null,
    new_appointment_date: safeIso(newAppointmentDate) || null,
    actor_user_id: cleanActorUserId,
    payload: sanitizeReschedulePayload(payload),
  };

  const { data, error } = await supabase
    .from("appointment_change_events")
    .insert(eventPayload)
    .select(
      "id, appointment_id, event_type, old_appointment_date, new_appointment_date, actor_user_id, payload, created_at"
    )
    .maybeSingle();

  if (error) throw error;
  return data || null;
}

export async function listAppointmentChangeEvents(appointmentId) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId) return [];

  const { data, error } = await supabase
    .from("appointment_change_events")
    .select(
      "id, appointment_id, event_type, old_appointment_date, new_appointment_date, actor_user_id, payload, created_at"
    )
    .eq("appointment_id", cleanAppointmentId)
    .order("created_at", { ascending: true });

  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function loadLatestRequestedRescheduleEvent(appointmentId) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId) return null;

  const { data, error } = await supabase
    .from("appointment_change_events")
    .select(
      "id, appointment_id, event_type, old_appointment_date, new_appointment_date, actor_user_id, payload, created_at"
    )
    .eq("appointment_id", cleanAppointmentId)
    .eq("event_type", APPOINTMENT_CHANGE_EVENT_TYPES.RESCHEDULE_REQUESTED)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data || null;
}

export function summarizeLatestAppliedReschedule(events = []) {
  const rows = Array.isArray(events) ? events : [];
  const latestApplied = [...rows]
    .reverse()
    .find(
      (row) =>
        String(row?.event_type || "").trim().toLowerCase() ===
        APPOINTMENT_CHANGE_EVENT_TYPES.RESCHEDULE_APPLIED
    );

  if (!latestApplied) return null;

  return {
    oldAppointmentDate: safeIso(latestApplied.old_appointment_date) || null,
    newAppointmentDate: safeIso(latestApplied.new_appointment_date) || null,
    createdAt: safeIso(latestApplied.created_at) || null,
  };
}

export async function applyApprovedReschedule({ appointmentId, actorUserId }) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  const cleanActorUserId = String(actorUserId || "").trim();
  if (!cleanAppointmentId || !cleanActorUserId) {
    throw new Error("Missing reschedule approval context.");
  }

  const latestRequestedEvent = await loadLatestRequestedRescheduleEvent(cleanAppointmentId);
  if (!latestRequestedEvent?.new_appointment_date) {
    throw new Error("Missing requested appointment update.");
  }

  const { data: currentAppointment, error: currentError } = await supabase
    .from("appointments")
    .select(
      "id, appointment_date, status, consultation_type, practice_id, duration_minutes, booked_service_id, booked_service_name, booked_service_price, booked_service_currency"
    )
    .eq("id", cleanAppointmentId)
    .maybeSingle();

  if (currentError) throw currentError;
  if (!currentAppointment?.id) {
    throw new Error("Appointment not found.");
  }

  const requestedPayload = sanitizeReschedulePayload(latestRequestedEvent.payload) || {};
  const nextAppointmentDate =
    requestedPayload.appointment_date || safeIso(latestRequestedEvent.new_appointment_date);

  if (!nextAppointmentDate) {
    throw new Error("Missing requested appointment time.");
  }

  const update = {
    appointment_date: nextAppointmentDate,
    consultation_type:
      requestedPayload.consultation_type || currentAppointment.consultation_type || "private",
    practice_id: hasOwn(requestedPayload, "practice_id")
      ? requestedPayload.practice_id
      : currentAppointment.practice_id || null,
    duration_minutes:
      requestedPayload.duration_minutes || currentAppointment.duration_minutes || 15,
    booked_service_id: hasOwn(requestedPayload, "booked_service_id")
      ? requestedPayload.booked_service_id
      : currentAppointment.booked_service_id || null,
    booked_service_name: hasOwn(requestedPayload, "booked_service_name")
      ? requestedPayload.booked_service_name
      : currentAppointment.booked_service_name || null,
    booked_service_price: hasOwn(requestedPayload, "booked_service_price")
      ? requestedPayload.booked_service_price
      : currentAppointment.booked_service_price ?? null,
    booked_service_currency: hasOwn(requestedPayload, "booked_service_currency")
      ? requestedPayload.booked_service_currency
      : currentAppointment.booked_service_currency || null,
    status: requestedPayload.status || currentAppointment.status || "scheduled",
    status_reason: null,
    superseded_by_appointment_id: null,
    updated_at: new Date().toISOString(),
  };

  const { data: updatedAppointment, error: updateError } = await supabase
    .from("appointments")
    .update(update)
    .eq("id", cleanAppointmentId)
    .select(
      "id, appointment_date, status, consultation_type, practice_id, duration_minutes, booked_service_id, booked_service_name, booked_service_price, booked_service_currency"
    )
    .maybeSingle();

  if (updateError) throw updateError;

  await createAppointmentChangeEvent({
    appointmentId: cleanAppointmentId,
    eventType: APPOINTMENT_CHANGE_EVENT_TYPES.RESCHEDULE_APPLIED,
    oldAppointmentDate:
      latestRequestedEvent.old_appointment_date || currentAppointment.appointment_date || null,
    newAppointmentDate: nextAppointmentDate,
    actorUserId: cleanActorUserId,
    payload: requestedPayload,
  });

  return {
    latestRequestedEvent,
    updatedAppointment: updatedAppointment || null,
  };
}
